import type { Entry } from "contentful";

const symbs = {
	moduleTitleRichText: "moduleTitleRichText",
	moduleStack: "appModuleImpactStack",
	moduleFAQ: "moduleFaq",
	stack: "stack",
	block: "block",
	sectionTitle: "sectionTitle",
	faqList: "faqList",
	else: () => true,
};

// utils
const undefinedFallback = (val, fallback = null) =>
	typeof val !== "undefined" ? val : fallback;

const cond = (...pCPairs) => {
	const pCPair = pCPairs.find(([predicate, _]) => predicate());
	const [_, consequent] = pCPair || [null, null];
	return consequent;
};

// module methods
const getContentType = (m) => undefinedFallback(m?.sys?.contentType?.sys?.id);

const isTitleRichTextModule = (m) =>
	getContentType(m) === symbs.moduleTitleRichText;

const isStackModule = (m) => getContentType(m) === symbs.moduleStack;

const isBlockModule = (m) =>
	isStackModule(m) && undefinedFallback(m?.fields?.isBlock, null);

const isFAQModule = (m) => getContentType(m) === symbs.moduleFAQ;

const makeSectionTitle = (imageTextModule) => ({
	type: symbs.sectionTitle,
	title: imageTextModule.fields.title,
	content: imageTextModule.fields.content,
	iconRef: imageTextModule.fields?.iconRef,
	id: imageTextModule.sys?.id,
});

const makeStack = (stackModule) => {
	const makeStackCard = (stackCardModule) => {
		const url = stackCardModule.fields?.mainImage?.fields?.file?.url;

		return {
			imageURL: url ? `https:${url}` : "",
			title: stackCardModule.fields?.headline,
			content: stackCardModule.fields?.mainText,
			id: stackCardModule.sys?.id,
		};
	};

	return {
		type: symbs.stack,
		title: stackModule.fields?.title,
		cards: stackModule.fields?.items?.map(makeStackCard),
		isStacked: stackModule.fields?.isStacked,
		id: stackModule.sys?.id,
	};
};

const makeBlock = (blockModule) => {
	const blockItems = blockModule.fields.items.map(transformModule);

	return {
		type: symbs.block,
		backgroundColor: blockModule.fields?.backgroundColor,
		items: blockItems,
		id: blockModule.sys?.id,
	};
};

const makeFAQList = (faqModule) => {
	const makeFAQItem = (faqItemModule) => ({
		title: faqItemModule.fields?.title,
		content: faqItemModule.fields?.content,
		id: faqItemModule.sys?.id,
	});

	return {
		type: symbs.faqList,
		title: faqModule.fields?.title,
		items: faqModule.fields?.items?.map(makeFAQItem),
		id: faqModule.sys?.id,
	};
};

const getModuleMaker = (m) =>
	cond(
		[() => isTitleRichTextModule(m), makeSectionTitle],
		[() => isBlockModule(m), makeBlock],
		[() => isStackModule(m), makeStack],
		[() => isFAQModule(m), makeFAQList],
		[symbs.else, () => ({})],
	);

// defined using function kw to make use of JS hoisting
function transformModule(m) {
	return getModuleMaker(m)(m);
}

export type CombinedPageEntry = Entry<{
	name: string;
	extraData: string | null;
	modules: Array<Entry<Object>>;
}>;

class ImpactPage {
	public static EntryTypes = {
		stack: symbs.stack,
		block: symbs.block,
		sectionTitle: symbs.sectionTitle,
		faqList: symbs.faqList,
	};

	public static EmptyConstructorParams: any = {
		fields: {
			name: "",
			extraData: null,
			modules: [],
		},
	};

	public static ConstructEmpty = (): ImpactPage => {
		return new ImpactPage(ImpactPage.EmptyConstructorParams);
	};

	public static Construct = (pageEntry: CombinedPageEntry): ImpactPage => {
		return new ImpactPage(pageEntry);
	};

	pageEntry: CombinedPageEntry;

	constructor(pageEntry: CombinedPageEntry) {
		this.pageEntry = pageEntry;
	}

	get totalTrees(): string | null {
		return this.pageEntry.fields?.extraData;
	}

	get items(): Array<Object> {
		return this.pageEntry.fields?.modules.map(transformModule);
	}
}

export default ImpactPage;
